export const DEFAULT_LOCALE = "en";

export const LOCALE_URL_SEGMENTS = {
  en: "en",
  zh: "zh-cn",
  "zh-TW": "zh-tw",
  fr: "fr",
  es: "es",
  ar: "ar",
  ja: "ja",
  ru: "ru",
  ko: "ko",
  pt: "pt",
  "pt-BR": "pt-br",
  it: "it",
  de: "de",
  hi: "hi",
  mn: "mn",
  th: "th",
  uk: "uk",
  vi: "vi",
  id: "id",
  tl: "tl",
  bn: "bn",
};

export const SUPPORTED_LOCALES = Object.keys(LOCALE_URL_SEGMENTS);

const URL_SEGMENT_TO_LOCALE = Object.entries(LOCALE_URL_SEGMENTS).reduce(
  (acc, [locale, segment]) => {
    acc[segment] = locale;
    return acc;
  },
  {},
);

const LOCALE_ALIASES = {
  "en-us": "en",
  "en-gb": "en",
  "zh-cn": "zh",
  "zh-hans": "zh",
  "zh-sg": "zh",
  "zh-tw": "zh-TW",
  "zh-hk": "zh-TW",
  "zh-hant": "zh-TW",
  "pt-br": "pt-BR",
};

export function normalizeLocale(locale) {
  if (!locale) return DEFAULT_LOCALE;
  const raw = String(locale).trim();
  if (!raw) return DEFAULT_LOCALE;
  if (SUPPORTED_LOCALES.includes(raw)) return raw;

  const normalized = raw.replace(/_/g, "-");
  if (SUPPORTED_LOCALES.includes(normalized)) return normalized;

  const lower = normalized.toLowerCase();
  if (URL_SEGMENT_TO_LOCALE[lower]) return URL_SEGMENT_TO_LOCALE[lower];
  if (LOCALE_ALIASES[lower]) return LOCALE_ALIASES[lower];

  const primary = lower.split("-")[0];
  if (SUPPORTED_LOCALES.includes(primary)) return primary;

  return DEFAULT_LOCALE;
}

export function isSupportedLocale(locale) {
  const raw = String(locale || "").trim();
  return SUPPORTED_LOCALES.includes(raw) || Boolean(URL_SEGMENT_TO_LOCALE[raw.toLowerCase()]);
}

export function localeToUrlSegment(locale) {
  return LOCALE_URL_SEGMENTS[normalizeLocale(locale)] || LOCALE_URL_SEGMENTS[DEFAULT_LOCALE];
}

export function localeToHtmlLang(locale) {
  const normalized = normalizeLocale(locale);
  switch (normalized) {
    case "zh":
      return "zh-CN";
    case "zh-TW":
      return "zh-TW";
    case "pt-BR":
      return "pt-BR";
    default:
      return normalized;
  }
}

export function localeToOgLocale(locale) {
  const normalized = normalizeLocale(locale);
  switch (normalized) {
    case "en":
      return "en_US";
    case "zh":
      return "zh_CN";
    case "zh-TW":
      return "zh_TW";
    case "pt-BR":
      return "pt_BR";
    default:
      return normalized.replace(/-/g, "_");
  }
}

function splitPathParts(pathname = "/") {
  return String(pathname || "/")
    .split("/")
    .filter(Boolean);
}

function splitPathQueryHash(path = "/") {
  const [pathAndQuery, hash = ""] = String(path || "/").split("#");
  const [pathname = "/", search = ""] = pathAndQuery.split("?");
  return {
    pathname: pathname || "/",
    search: search ? `?${search}` : "",
    hash: hash ? `#${hash}` : "",
  };
}

export function getLocaleFromPathname(pathname = "/") {
  const [firstSegment] = splitPathParts(pathname);
  if (!firstSegment) return null;
  const locale = URL_SEGMENT_TO_LOCALE[String(firstSegment).toLowerCase()];
  return locale || null;
}

export function stripLocalePrefix(pathname = "/") {
  const parts = splitPathParts(pathname);
  if (!parts.length) return "/";
  if (!URL_SEGMENT_TO_LOCALE[String(parts[0]).toLowerCase()]) return pathname || "/";
  const stripped = `/${parts.slice(1).join("/")}`;
  return stripped === "/" ? "/" : stripped.replace(/\/{2,}/g, "/");
}

export function localizePath(path = "/", locale = DEFAULT_LOCALE) {
  const { pathname, search, hash } = splitPathQueryHash(path);
  const normalizedLocale = normalizeLocale(locale);
  const cleanPath = stripLocalePrefix(pathname || "/");
  const normalizedPath =
    cleanPath === "/" ? "/" : cleanPath.replace(/\/{2,}/g, "/").replace(/\/$/, "");

  if (normalizedLocale === DEFAULT_LOCALE) {
    return `${normalizedPath}${search}${hash}` || "/";
  }

  const prefix = `/${localeToUrlSegment(normalizedLocale)}`;
  const suffixedPath = normalizedPath === "/" ? prefix : `${prefix}${normalizedPath}`;
  return `${suffixedPath}${search}${hash}`;
}

export function buildLocalizedUrl(path, origin, locale) {
  const localizedPath = localizePath(path, locale);
  return new URL(localizedPath, origin).toString();
}

export function getAlternateLanguageLinks(path, origin) {
  const alternates = SUPPORTED_LOCALES.map((locale) => ({
    hrefLang: localeToHtmlLang(locale).toLowerCase(),
    href: buildLocalizedUrl(path, origin, locale),
  }));

  alternates.push({
    hrefLang: "x-default",
    href: buildLocalizedUrl(path, origin, DEFAULT_LOCALE),
  });

  return alternates;
}
